import React, { useMemo } from "react";
import {
    DataTableColumn,
    DataTableRow,
} from "Shared/components/tables/data-table";
import { useTranslation } from "Shared/util/custom-hooks";
import { translateColumns } from "Shared/util/translations";
import { PickOff } from "Shared/util/types";
import { isCriticalConsentStatus } from "../../../../utils";
import { ColoredText } from "../components/ColoredText";
import { DataTableRadioGroup } from "../components/DataTableRadioGroup";
import { DataTableMultipleSelect } from "../components/DataTableMultipleSelect";
import { DataTableItem } from "../types";

export const useDataTableColumns = (
    onRadioChange: (item: DataTableItem) => void,
    onSelectedCurrenciesChange: (
        supportedCurrencies: DataTableItem["supportedCurrencies"],
        item: PickOff<DataTableItem, "supportedCurrencies">,
    ) => void,
): DataTableColumn<DataTableItem>[] => {
    const t = useTranslation(
        (x) => x.company_settings.payoutInformationPage.payoutAccountDataTable,
    );

    const columns = useMemo(
        (): DataTableColumn<DataTableItem>[] => [
            {
                customCellRenderer: (item: DataTableRow<DataTableItem>) => (
                    <DataTableRadioGroup
                        item={item}
                        onRadioChange={onRadioChange}
                    />
                ),
                key: "isPrimary",
                title: "primary",
            },
            {
                customCellRenderer: ({
                    bank: { name },
                    consentStatus,
                }: DataTableRow<DataTableItem>) => (
                    <ColoredText
                        color={
                            isCriticalConsentStatus(consentStatus)
                                ? "red"
                                : "default"
                        }
                        content={name}
                    />
                ),
                key: "bank.name",
                title: "bank",
            },
            {
                customCellRenderer: ({
                    consentStatus,
                    isExpressPaymentMethod,
                }: DataTableRow<DataTableItem>) => (
                    <ColoredText
                        color={
                            isCriticalConsentStatus(consentStatus)
                                ? "red"
                                : "default"
                        }
                        content={isExpressPaymentMethod ? t.yes : t.no}
                    />
                ),
                key: "isExpressPaymentMethod",
                title: "express",
            },
            {
                customCellRenderer: ({
                    accountName,
                    accountNumber,
                    consentStatus,
                }: DataTableRow<DataTableItem>) => (
                    <ColoredText
                        color={
                            isCriticalConsentStatus(consentStatus)
                                ? "red"
                                : "default"
                        }
                        content={
                            <>
                                <div>{accountNumber}</div>
                                <div
                                    style={{
                                        fontSize: "0.75rem",
                                        lineHeight: "1",
                                    }}
                                >
                                    {accountName}
                                </div>
                            </>
                        }
                    />
                ),
                key: "accountNumber",
                title: "account",
            },
            {
                customCellRenderer: ({
                    consentStatus,
                    currency,
                }: DataTableRow<DataTableItem>) => (
                    <ColoredText
                        color={
                            isCriticalConsentStatus(consentStatus)
                                ? "red"
                                : "default"
                        }
                        content={currency}
                    />
                ),
                key: "currency",
                title: "currency",
            },
            {
                customCellRenderer: (item: DataTableRow<DataTableItem>) => (
                    <DataTableMultipleSelect
                        item={item}
                        onSelectedCurrenciesChange={onSelectedCurrenciesChange}
                    />
                ),
                key: "supportedCurrencies",
                title: "supportedCurrencies",
            },
            {
                customCellRenderer: ({
                    consentStatus,
                    userName,
                }: DataTableRow<DataTableItem>) => (
                    <ColoredText
                        color={
                            isCriticalConsentStatus(consentStatus)
                                ? "red"
                                : "default"
                        }
                        content={userName ?? t.customerService}
                    />
                ),
                key: "userName",
                title: "addedBy",
            },
        ],
        [onRadioChange, onSelectedCurrenciesChange, t],
    );

    const translatedColumns = useMemo(
        () => translateColumns(columns, t),
        [columns, t],
    );

    return translatedColumns;
};
